// objectivesManager.util.ts

import { auth, db, firebaseImport, FirebaseTimestamp } from '../../config/main_config'; // Adjust the path as needed
import { createToast } from './createToast.util'; // Utility for showing toast notifications
import { closeModal } from './closeModal.util'; // Utility for closing modals
import { waitForCompanyID } from './waitForCompanyID.util';
import { openModal } from './openModal.util';
import Tableordion from './tableordion.util';
import { sanitise } from './sanitise.util';

/**
 * Interface representing an Objective.
 */
interface Objective {
    id: string;
    addedBy: string;
    dateAdded: firebaseImport.firestore.Timestamp;
    dateUpdated: firebaseImport.firestore.Timestamp;
    owner: string;
    objectiveText: string;
    objectiveTargetDate: firebaseImport.firestore.Timestamp;
    updatedBy: string;
}

/**
 * Fetches objectives data from Firestore.
 * 
 * @returns A promise that resolves to an array of Objective objects.
 */
export async function fetchObjectives(): Promise<Objective[]> {
    const companyID = await waitForCompanyID(); // Dynamic retrieval of companyID
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        if (doc.exists) {
            const data = doc.data()?.ismsObjectives;
            if (data && typeof data === 'object') {
                const objectives: Objective[] = Object.entries(data).map(([id, objective]: [string, any]) => ({
                    id,
                    addedBy: objective.addedBy,
                    dateAdded: objective.dateAdded instanceof FirebaseTimestamp ? objective.dateAdded.toDate() : 'Invalid Date',
                    dateUpdated: objective.dateUpdated instanceof FirebaseTimestamp ? objective.dateUpdated.toDate() : 'Invalid Date',
                    owner: objective.owner,
                    objectiveText: objective.objectiveText,
                    objectiveTargetDate: objective.objectiveTargetDate instanceof FirebaseTimestamp ? objective.objectiveTargetDate.toDate() : 'Invalid Date',
                    updatedBy: objective.updatedBy,
                }));
                return objectives;
            } else {
                console.warn('No objectives data found.');
                return [];
            }
        } else {
            console.warn('Objectives document does not exist.');
            return [];
        }
    } catch (error) {
        console.error('Error fetching objectives:', error);
        createToast({
            message: 'Failed to load objectives.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return [];
    }
}

/**
 * Renders the objectives into a tableordion.
 * 
 * @param objectives - Array of Objective objects.
 */
export async function renderObjectivesTable(objectives: Objective[]): Promise<void> {
    const tableContainer = document.querySelector('#objectivesTableordion .tableordion-body');

    if (!tableContainer) {
        console.error('Objectives table container not found.');
        return;
    }

    let html = `
    `;

    // Sort the objectives by ID
    objectives.sort((a, b) => parseInt(a.id) - parseInt(b.id));

    objectives.forEach(objective => {
        html += `
            <tr data-id="${objective.id}" class="tableordion-row">
                <td>${objective.id}</td>
                <td>${sanitise(objective.objectiveText)}</td>
                <td>${(objective.objectiveTargetDate as unknown as Date).toLocaleDateString()}</td>
                <td>
                    <button class="action-btn  edit-objective-btn" data-id="${objective.id}">
                        <i class="fa-duotone fa-solid fa-pencil edit-icon" alt="Edit"></i>
                    </button>
                    <button class="action-btn delete-objective-btn" data-id="${objective.id}">
                        <i class="fa-duotone fa-trash delete-icon color-red" alt="Delete"></i>
                    </button>
                </td>
            </tr>
        `;
    });

    html += `

    `;

    tableContainer.innerHTML = html;

    const accordions = new Tableordion('.tableordion-row')

    // Attach event listeners to edit and delete buttons
    attachObjectiveButtonsListeners();
}

/**
 * Attaches event listeners to all edit and delete buttons in the tableordion.
 */
function attachObjectiveButtonsListeners(): void {
    const editButtons = document.querySelectorAll('.edit-objective-btn');
    const deleteButtons = document.querySelectorAll('.delete-objective-btn');

    editButtons.forEach(button => {
        button.addEventListener('click', async (event) => {
            const target = event.currentTarget as HTMLElement;
            const objectiveId = target.getAttribute('data-id');

            if (!objectiveId) {
                console.error('Objective ID not found on the edit button.');
                return;
            }

            const objectives = (window as any).objectives as Objective[];
            const objective = objectives.find(obj => obj.id === objectiveId);

            if (!objective) {
                console.error('Objective not found for the given ID.');
                return;
            }

            openEditObjectiveModal(objective);
        });
    });

    deleteButtons.forEach(button => {
        button.addEventListener('click', async (event) => {
            const target = event.currentTarget as HTMLElement;
            const objectiveId = target.getAttribute('data-id');

            if (!objectiveId) {
                console.error('Objective ID not found on the delete button.');
                return;
            }

            // Confirm deletion
            if (confirm('Are you sure you want to delete this objective?')) {
                await deleteObjective(objectiveId);
            }
        });
    });
}

/**
 * Opens the edit objective modal and populates it with current data.
 * 
 * @param objective - The Objective object to edit.
 */
export function openEditObjectiveModal(objective: Objective): void {
    const modal = document.querySelector('[data-managedid="EditObjectiveModal"]') as HTMLElement;
    if (!modal) {
        console.error('Edit Objective Modal not found.');
        return;
    }

    // Populate the modal fields
    const objectiveIdElement = modal.querySelector('#objectiveId_edit') as HTMLInputElement;
    const objectiveTextElement = modal.querySelector('#objectiveText_edit') as HTMLInputElement;
    const objectiveTargetDateElement = modal.querySelector('#objectiveTargetDate_edit') as HTMLInputElement;
    const dateAddedElement = modal.querySelector('#dateAdded_edit') as HTMLInputElement;

    if (objectiveIdElement && objectiveTextElement && objectiveTargetDateElement && dateAddedElement) {
        modal.dataset.id = objective.id;
        objectiveIdElement.value = objective.id;
        objectiveTextElement.value = objective.objectiveText;
        objectiveTargetDateElement.valueAsDate = (objective.objectiveTargetDate as unknown as Date);
        dateAddedElement.valueAsDate = (objective.dateAdded as unknown as Date);
    } else {
        console.error('One or more modal input elements not found.');
        return;
    }

    // Open the modal using your existing modal management function
    openModal('[data-managedid="EditObjectiveModal"]');
}

/**
 * Adds a new objective to Firestore.
 * 
 * @param objectiveText - The objective text.
 * @param objectiveTargetDate - The target date for the objective.
 */
export async function addObjective(objectiveText: string, objectiveTargetDate: Date, objectiveDateAdded: Date): Promise<void> {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        let ismsObjectives = doc.data()?.ismsObjectives;

        if (!ismsObjectives || typeof ismsObjectives !== 'object') {
            ismsObjectives = {};
        }

        // Generate a new ID
        const newId = generateNewId(ismsObjectives);

        // Create the new objective object
        const newObjective: Objective = {
            id: newId,
            addedBy: auth.currentUser.uid, // Replace with actual user ID
            dateAdded: firebaseImport.firestore.Timestamp.now(),
            dateUpdated: firebaseImport.firestore.Timestamp.now(),
            owner: auth.currentUser.uid, // Replace with actual user ID
            objectiveText,
            objectiveTargetDate: firebaseImport.firestore.Timestamp.fromDate(objectiveTargetDate),
            updatedBy: auth.currentUser.uid, // Replace with actual user ID
        };

        // Add the new objective to the existing map
        ismsObjectives[newId] = {
            addedBy: newObjective.addedBy,
            dateAdded: objectiveDateAdded,
            dateUpdated: newObjective.dateUpdated,
            owner: newObjective.owner,
            objectiveText: newObjective.objectiveText,
            objectiveTargetDate: newObjective.objectiveTargetDate,
            updatedBy: newObjective.updatedBy,
        };

        // Update Firestore
        await docRef.update({
            ismsObjectives
        });

        createToast({
            message: 'Objective added successfully.',
            customStyles: {
                backgroundColor: '#28a745',
                color: '#ffffff'
            }
        });

    } catch (error) {
        console.error('Error adding objective:', error);
        createToast({
            message: 'Failed to add objective.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    }
}

/**
 * Edits an existing objective in Firestore.
 * 
 * @param id - The ID of the objective to edit.
 * @param objectiveText - The updated objective text.
 * @param objectiveTargetDate - The updated target date for the objective.
 */
export async function editObjective(id: string, objectiveText: string, objectiveTargetDate: Date, objectiveDateAdded: Date): Promise<void> {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        let ismsObjectives = doc.data()?.ismsObjectives;

        if (!ismsObjectives || typeof ismsObjectives !== 'object' || !ismsObjectives[id]) {
            console.error('Objective not found.');
            createToast({
                message: 'Objective not found.',
                customStyles: {
                    backgroundColor: '#ff0000',
                    color: '#ffffff'
                }
            });
            return;
        }

        // Update the objective
        ismsObjectives[id] = {
            ...ismsObjectives[id],
            objectiveText,
            objectiveTargetDate: objectiveTargetDate,
            dateAdded: objectiveDateAdded, // Replace with actual date added value
            dateUpdated: firebaseImport.firestore.Timestamp.now().toDate(),
            updatedBy: auth.currentUser.uid, // Replace with actual user ID
        };

        // Update Firestore
        await docRef.update({
            ismsObjectives
        });

        createToast({
            message: 'Objective updated successfully.',
            customStyles: {
                backgroundColor: '#28a745',
                color: '#ffffff'
            }
        });

    } catch (error) {
        console.error('Error editing objective:', error);
        createToast({
            message: 'Failed to edit objective.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    }
}

/**
 * Deletes an objective from Firestore.
 * 
 * @param id - The ID of the objective to delete.
 */
export async function deleteObjective(id: string): Promise<void> {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);

    try {
        await docRef.update({
            [`ismsObjectives.${id}`]: firebaseImport.firestore.FieldValue.delete()
        });

        createToast({
            message: 'Objective deleted successfully.',
            customStyles: {
                backgroundColor: '#28a745',
                color: '#ffffff'
            }
        });

    } catch (error) {
        console.error('Error deleting objective:', error);
        createToast({
            message: 'Failed to delete objective.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    }
}

/**
 * Sets up a real-time listener for objectives using onSnapshot.
 */
export function setupObjectivesListener(): void {
    waitForCompanyID().then(companyID => {
        const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
        const docRef = db.doc(docPath);

        docRef.onSnapshot(docSnapshot => {
            if (docSnapshot.exists) {
                const data = docSnapshot.data()?.ismsObjectives;
                if (data && typeof data === 'object') {
                    const objectives: Objective[] = Object.entries(data).map(([id, objective]: [string, any]) => ({
                        id,
                        addedBy: objective.addedBy,
                        dateAdded: objective.dateAdded instanceof FirebaseTimestamp ? objective.dateAdded.toDate() : 'Invalid Date',
                        dateUpdated: objective.dateUpdated instanceof FirebaseTimestamp ? objective.dateUpdated.toDate() : 'Invalid Date',
                        owner: objective.owner,
                        objectiveText: objective.objectiveText,
                        objectiveTargetDate: objective.objectiveTargetDate instanceof FirebaseTimestamp ? objective.objectiveTargetDate.toDate() : 'Invalid Date',
                        updatedBy: objective.updatedBy,
                    }));
                    (window as any).objectives = objectives;
                    renderObjectivesTable(objectives);
                }
            }
        }, error => {
            console.error('Error with onSnapshot listener:', error);
            createToast({
                message: 'Real-time updates failed.',
                customStyles: {
                    backgroundColor: '#ff0000',
                    color: '#ffffff'
                }
            });
        });
    });
}

/**
 * Generates a new unique ID for an objective.
 * 
 * @param ismsObjectives - The current objectives map.
 * @returns A new unique ID as a string.
 */
function generateNewId(ismsObjectives: { [key: string]: any }): string {
    const existingIds = Object.keys(ismsObjectives).map(id => parseInt(id, 10)).filter(id => !isNaN(id));
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
    return (maxId + 1).toString();
}